import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { useCoursesLoaderData } from "../coursesLoader.ts";
import {
  formatRemaining,
  getDaysRemaining,
  getHoursRemaining,
  getMinutesRemaining,
} from "../timeHelper.tsx";
import { useCallback, useEffect, useState } from "react";
import styles from "./ProgramBanner.module.scss";
import { clsx } from "clsx";
import AnalogCharacters from "common/core-ui/Analog/AnalogCharacters.tsx";
import useTermOptDeadline from "../useTermOptDeadline.tsx";
import ProgramSwitcher from "../ProgramSwitcher";
import useSwitchProgram from "../useSwitchProgram.tsx";
import { useLayoutData } from "../../layout/myCoursesLayoutLoader.ts";
import ProgramBannerButton from "./ProgramBannerButton.tsx";
import BaseLinkOrButton from "common/core-ui/BaseLinkOrButton.tsx";
import useVirtualStore from "../useVirtualStore.tsx";
import ProgramBannerAddAllButton from "./ProgramBannerAddAllButton.tsx";
import useInstitutionProgram from "../useInstitutionProgram.tsx";
import useTermStoreMetrics from "../useTermStoreMetrics.tsx";

export default function ProgramBanner() {
  const intl = useIntl();
  const courseData = useCoursesLoaderData();
  const { config } = useLayoutData();
  const { currentTerm } = courseData;
  const institutionProgram = currentTerm?.institutionProgram;
  const programDecision = currentTerm?.programDecision;
  const [daysRemaining, setDaysRemaining] = useState<number>();
  const [hoursRemaining, setHoursRemaining] = useState<number>();
  const [minutesRemaining, setMinutesRemaining] = useState<number>(23);
  const { optWindowOpen, optDeadline } = useTermOptDeadline();
  const [isProgramSwitcherOpen, setIsProgramSwitcherOpen] = useState(false);
  const { changeProgram, isProgramChangeProcessing } = useSwitchProgram();
  const { isVirtualStore } = useVirtualStore();
  const { requiredToPurchaseBaseCount } = useTermStoreMetrics();
  const { isEquitableAccessProgram } = useInstitutionProgram();
  const isEAOptedOut =
    programDecision === "no_program" && isEquitableAccessProgram;
  const hideForStore = isVirtualStore && requiredToPurchaseBaseCount === 0;
  const programAllowsBanner = () => {
    if (institutionProgram === "equitable_access" && !isVirtualStore) {
      return config.equitable_access.catalogLevelOptOut;
    }
    return true;
  };
  const showBanner =
    programAllowsBanner() &&
    optWindowOpen &&
    courseData.courses.some((course) => course.userRole === "learner") &&
    !hideForStore;
  const showCountdown =
    showBanner && daysRemaining !== undefined && daysRemaining <= 99;

  const updateRemaining = useCallback(() => {
    if (!optDeadline) {
      return;
    }
    setDaysRemaining(getDaysRemaining(optDeadline));
    setHoursRemaining(getHoursRemaining(optDeadline));
    setMinutesRemaining(getMinutesRemaining(optDeadline));
  }, [optDeadline]);

  useEffect(() => {
    updateRemaining();
    if (showCountdown) {
      const intervalId = setInterval(updateRemaining, 1000);
      return () => clearInterval(intervalId);
    }
  }, [showCountdown, updateRemaining]);

  const bannerTitle = () => {
    if (isVirtualStore) {
      if (isEAOptedOut) {
        return intl.formatMessage(
          { defaultMessage: "{program} could save you money" },
          { program: config.equitable_access.programName }
        );
      }
      return intl.formatMessage({
        defaultMessage: "The countdown is on to get your course materials",
      });
    }

    switch (programDecision) {
      case "equitable_access":
        return intl.formatMessage(
          { defaultMessage: "You're enrolled in {program}" },
          { program: config.equitable_access.programName }
        );
      case "inclusive_access":
        return intl.formatMessage(
          { defaultMessage: "You're enrolled in {program}" },
          { program: config.inclusive_access.programName }
        );
      default:
        return config.no_program.programName;
    }
  };

  const bannerDescription = () => {
    if (isVirtualStore) {
      if (isEAOptedOut) {
        return intl.formatMessage(
          {
            defaultMessage:
              "By opting back in to {program} you could save money on your course materials.",
          },
          { program: config.equitable_access.programName }
        );
      }
      return intl.formatMessage({
        defaultMessage:
          "You’ll be the most successful if you have your course materials on the first day of class.",
      });
    }

    switch (programDecision) {
      case "equitable_access":
        return config.equitable_access.programDescription;
      case "inclusive_access":
        return config.inclusive_access.programDescription;
      default:
        return config.no_program.programDescription;
    }
  };

  const getBannerButton = () => {
    if (courseData.currentTerm?.institutionProgram === "flexible_access") {
      return (
        <div className={styles.bannerButtonContainer}>
          <ProgramBannerButton
            data-testid="change-programs"
            onClick={() => setIsProgramSwitcherOpen(true)}
          >
            {programDecision === "no_program" ? (
              <FormattedMessage defaultMessage="Select Programs" />
            ) : (
              <FormattedMessage defaultMessage="Switch Programs" />
            )}
          </ProgramBannerButton>
        </div>
      );
    } else if (
      courseData.currentTerm?.institutionProgram === "equitable_access"
    ) {
      const programName = config.equitable_access.programName;
      return (
        <div className={styles.bannerButtonContainer}>
          <ProgramBannerButton
            data-testid="ea-change-program"
            onClick={() => {
              courseData.currentTerm?.programDecision === "no_program"
                ? changeProgram("equitable_access")
                : setIsProgramSwitcherOpen(true);
            }}
            disabled={isProgramChangeProcessing}
          >
            {courseData.currentTerm?.programDecision === "no_program" ? (
              <FormattedMessage
                defaultMessage="Opt In to {programName}"
                values={{ programName }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Opt Out of {programName}"
                values={{ programName }}
              />
            )}
          </ProgramBannerButton>
          {!isVirtualStore && (
            <BaseLinkOrButton
              className="vst-link vst-link-variant-white"
              onClick={() => setIsProgramSwitcherOpen(true)}
            >
              <FormattedMessage defaultMessage="View Program Details" />
            </BaseLinkOrButton>
          )}
        </div>
      );
    }

    if (isVirtualStore) {
      return (
        <ProgramBannerAddAllButton
          wrapperClassName={styles.bannerButtonContainer}
        />
      );
    }
  };

  const showTimer = (
    remainingTime: number,
    remainingTitle: JSX.Element,
    testId: string
  ) => {
    return (
      <div className={styles.remainingContainer} data-testid={testId}>
        <div className={styles.remainingTitle}>{remainingTitle}</div>
        <div className={styles.remaining}>
          <AnalogCharacters
            fontSize="3rem"
            characters={formatRemaining(remainingTime)}
            gap="0.2rem"
          />
        </div>
      </div>
    );
  };

  const showDays = () => {
    const remainingTitle = <FormattedMessage defaultMessage="Days" />;
    const testId = "days-remaining";
    if (daysRemaining === undefined) {
      return;
    }
    return (
      <>
        <div className={styles.remainingContainerSmall}>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {daysRemaining} {remainingTitle},
        </div>
        {showTimer(daysRemaining, remainingTitle, testId)}
      </>
    );
  };

  const showHours = () => {
    const remainingTitle = <FormattedMessage defaultMessage="Hours" />;
    const testId = "hours-remaining";
    if (hoursRemaining === undefined) {
      return;
    }
    return (
      <>
        <div className={styles.remainingContainerSmall}>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {hoursRemaining} {remainingTitle},
        </div>
        {showTimer(hoursRemaining, remainingTitle, testId)}
      </>
    );
  };

  const showMinutes = () => {
    const remainingTitle = <FormattedMessage defaultMessage="Minutes" />;
    const testId = "minutes-remaining";
    if (minutesRemaining === undefined) {
      return;
    }
    return (
      <>
        <div className={styles.remainingContainerSmall}>
          {minutesRemaining} {remainingTitle}
        </div>
        {showTimer(minutesRemaining, remainingTitle, testId)}
      </>
    );
  };

  const showCountdownLabel = () => {
    if (isVirtualStore) {
      if (isEAOptedOut) {
        return <FormattedMessage defaultMessage="remaining to opt in" />;
      } else {
        return <FormattedMessage defaultMessage="until the term starts" />;
      }
    } else {
      return daysRemaining ? (
        <FormattedMessage defaultMessage="remaining to make your course material decisions" />
      ) : (
        <FormattedMessage defaultMessage="Today is the last day to make your course material decisions" />
      );
    }
  };

  const bannerBtn = getBannerButton();

  return (
    <>
      {showBanner ? (
        <section className={styles.container} data-testid="program-banner">
          <div
            className={clsx(styles.banner, bannerBtn && styles.bannerHasAction)}
          >
            {showCountdown && (
              <div
                data-testid="time-remaining"
                className={styles.timeRemainingContainer}
              >
                <div className={styles.timeRemainingCount}>
                  {showDays()}
                  {showHours()}
                  {showMinutes()}
                </div>
                <div className={styles.timeRemainingText}>
                  {showCountdownLabel()}
                </div>
              </div>
            )}
            <div className={styles.contentHolder}>
              <div className={styles.textContent}>
                <h1 className={clsx("template-h2", styles.header)}>
                  {bannerTitle()}
                </h1>
                <div className={styles.description}>{bannerDescription()}</div>
              </div>
              {bannerBtn}
            </div>
          </div>
        </section>
      ) : null}
      {isProgramSwitcherOpen && (
        <ProgramSwitcher
          onRequestClose={() => setIsProgramSwitcherOpen(false)}
        />
      )}
    </>
  );
}
